import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Power, Pencil, X, Archive, Plus } from "lucide-react";
import QuestionForm from "../../components/QuestionForm";

export default function AdminArchive() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // question or null
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    const { data } = await api.get(`/admin/questions`);
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  const handleSave = async (form) => {
    try {
      if (editing) {
        await api.put(`/admin/questions/${editing.id}`, form);
        toast.success("Pergunta atualizada");
      } else {
        await api.post(`/admin/questions`, form);
        toast.success("Pergunta criada no arquivo");
      }
      setEditing(null);
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
      throw e;
    }
  };

  const toggleActive = async (q) => {
    try { await api.put(`/admin/questions/${q.id}`, { active: q.active === false }); load(); }
    catch { toast.error("Não foi possível alterar o estado"); }
  };

  const remove = async (q) => {
    const used = q.used_in || [];
    let msg = "Eliminar esta pergunta do arquivo? Esta ação é irreversível.";
    if (used.length > 0) {
      msg = `Atenção: esta pergunta faz parte de ${used.length} quiz(zes):\n\n• ${used.join("\n• ")}\n\nSe eliminar, será removida também desses quizzes. Deseja mesmo eliminar?`;
    }
    if (!window.confirm(msg)) return;
    await api.delete(`/admin/questions/${q.id}`);
    toast.success("Pergunta eliminada");
    if (editing?.id === q.id) { setEditing(null); setShowForm(false); }
    load();
  };

  const startEdit = (q) => { setEditing(q); setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const startNew = () => { setEditing(null); setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const filtered = items.filter((q) => {
    const s = search.trim().toLowerCase();
    if (!s) return true;
    return (q.identification || "").toLowerCase().includes(s) || (q.question_pt || "").toLowerCase().includes(s);
  });

  return (
    <div data-testid="admin-archive-page">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <h1 className="font-outfit text-3xl font-bold text-[#0B1D3A] flex items-center gap-2"><Archive size={26} /> Arquivo de Perguntas</h1>
        <button type="button" onClick={startNew} data-testid="archive-new-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0B1D3A] text-white text-sm font-bold">
          <Plus size={15} /> Nova pergunta
        </button>
      </div>
      <p className="text-sm text-[#0B1D3A]/55 mb-6">Todas as perguntas do sistema, por ordem alfabética da <strong>identificação</strong> (interna, nunca aparece no site). Editar aqui reflete-se em todos os quizzes onde a pergunta está.</p>

      {showForm && (
        <>
          <div className="flex items-center justify-between rounded-lg bg-[#FFF6E5] border border-[#FFD93D] px-4 py-2 mb-3 text-sm">
            <span className="font-semibold text-[#0B1D3A]">{editing ? `A editar: ${editing.identification || editing.question_pt}` : "Nova pergunta"}</span>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-[#0B1D3A]/60 inline-flex items-center gap-1" data-testid="archive-cancel-btn"><X size={14}/> Fechar</button>
          </div>
          <QuestionForm key={editing ? editing.id : "new"} initial={editing} onSubmit={handleSave} submitLabel={editing ? "Guardar alterações" : "Criar pergunta"} />
        </>
      )}

      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pesquisar por identificação ou pergunta..." className="w-full px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm mb-4" data-testid="archive-search-input" />

      <div className="rounded-2xl bg-white border border-[#0B1D3A]/5 overflow-x-auto">
        <table className="w-full text-sm" data-testid="archive-table">
          <thead>
            <tr className="text-left text-[#0B1D3A]/50 border-b border-[#0B1D3A]/10">
              <th className="px-4 py-3 font-semibold">Identificação</th>
              <th className="px-4 py-3 font-semibold">Pergunta (PT)</th>
              <th className="px-4 py-3 font-semibold">Usada em</th>
              <th className="px-4 py-3 font-semibold">Estado</th>
              <th className="px-4 py-3 font-semibold text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((q) => {
              const isActive = q.active !== false;
              const used = q.used_in || [];
              return (
                <tr key={q.id} className="border-b border-[#0B1D3A]/5 last:border-0" data-testid={`archive-row-${q.id}`}>
                  <td className="px-4 py-3 font-bold text-[#005F73] align-top" data-testid={`archive-ident-${q.id}`}>{q.identification || <span className="text-[#0B1D3A]/30 font-normal italic">(sem identificação)</span>}</td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-center gap-2">
                      {q.image_path && <img src={`${backendUrl}/api/files/${q.image_path}`} alt="" className="h-10 w-10 object-cover rounded-md shrink-0" />}
                      <span className="text-[#0B1D3A] line-clamp-2">{q.question_pt}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top text-xs text-[#0B1D3A]/70" data-testid={`archive-usedin-${q.id}`}>
                    {used.length === 0 ? (
                      <span className="text-[#0B1D3A]/35">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1 max-w-[240px]">
                        {used.map((name, k) => (
                          <span key={k} className="inline-block px-2 py-0.5 rounded-full bg-[#005F73]/10 text-[#005F73] font-semibold whitespace-nowrap">{name}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <button onClick={() => toggleActive(q)} data-testid={`archive-toggle-${q.id}`}
                      className={`text-xs px-2.5 py-1 rounded-md inline-flex items-center gap-1 font-bold ${isActive ? "bg-[#2D9C5A]/10 text-[#2D9C5A]" : "bg-[#0B1D3A]/5 text-[#0B1D3A]/50"}`}>
                      <Power size={12} /> {isActive ? "Ativa" : "Inativa"}
                    </button>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => startEdit(q)} className="text-xs px-3 py-1.5 rounded-md bg-[#0B1D3A]/5 inline-flex items-center gap-1" data-testid={`archive-edit-${q.id}`}><Pencil size={13}/> Editar</button>
                      <button onClick={() => remove(q)} className="text-xs px-3 py-1.5 rounded-md bg-red-50 text-red-600" data-testid={`archive-delete-${q.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan="5" className="px-4 py-8 text-center text-[#0B1D3A]/50">Nenhuma pergunta.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
